import { useMemo, useState } from 'react';
import { Section } from './NeSignalHelp';

// Authoring UI for a NEMA ring-barrier program. Target schema:
//
//   <tlLogic programID="NEMA" type="NEMA" offset="10">
//     <param key="total-cycle-length" value="130"/>
//     <param key="ring1" .../>  <param key="ring2" .../>
//     <param key="barrierPhases" .../>  <param key="barrier2Phases" .../>
//     <param key="coordinate-mode" value="true"/>
//     <param key="minRecall" value="2,6"/>  <param key="maxRecall" value=""/>
//     <phase minDur maxDur yellow red vehExt name state/>
//
// The editable quantity is a phase's SPLIT (green + yellow + all-red), not green time.
// INVARIANT: sum(splits in ring 1 per barrier side) == sum(ring 2 on that side), and
// side A + side B == cycle. A split is therefore never set directly — a change moves
// time to the next phase in the SAME ring on the SAME side; moving the barrier changes
// both rings. SUMO only rejects an unbalanced program at simulation time.
// A phase in the same slot of both rings has no opposing partner and is stored once.

const MIN_GREEN_FLOOR = 3;

const num = (v, dflt = 0) => (Number.isFinite(+v) ? +v : dflt);
const secs = (v) => `${Math.round(v)}s`;

// Slots 0,1 sit before the first barrier; slots 2,3 before the second.
const SIDE_OF_SLOT = [0, 0, 1, 1];

/** Which phase numbers each ring serves on each side, in cycle order. */
function layout(nema) {
  const r1 = nema.ring1 || [0, 0, 0, 0];
  const r2 = nema.ring2 || [0, 0, 0, 0];
  const sides = [[[], []], [[], []]]; // [side][ring] -> phase numbers
  const lockstep = new Set();
  for (let i = 0; i < 4; i++) {
    const s = SIDE_OF_SLOT[i];
    if (r1[i]) sides[s][0].push(r1[i]);
    if (r2[i]) sides[s][1].push(r2[i]);
    if (r1[i] && r1[i] === r2[i]) lockstep.add(r1[i]);
  }
  return { r1, r2, sides, lockstep };
}

/** Seed a program from a network with no splits to import: netconvert writes a uniform
 *  minDur/maxDur and puts the cycle length in every phase's duration. The cycle is
 *  split between barrier sides in proportion to phase count, then equally within each
 *  ring. An assumption, and the panel says so. */
function seed(nema, byNumber) {
  const { sides, lockstep } = layout(nema);
  const cycle = num(nema.cycle_s, 90) || 90;
  const depth = (s) => Math.max(sides[s][0].length, sides[s][1].length, 0);
  const dTotal = depth(0) + depth(1) || 1;
  const sideA = Math.round((cycle * depth(0)) / dTotal);

  const phases = {};
  const assign = (s, ring, total) => {
    const list = sides[s][ring];
    if (!list.length) return;
    const each = total / list.length;
    list.forEach((n, i) => {
      if (phases[n]) return; // lockstep phase already placed from the other ring
      const src = byNumber[n] || {};
      phases[n] = {
        split: i === list.length - 1 ? total - Math.round(each) * (list.length - 1)
                                     : Math.round(each),
        yellow: num(src.yellow, 3),
        red: num(src.red, 2),
        vehExt: num(src.vehext, 2),
        minGreen: num(src.minDur, 5),
      };
    });
  };
  assign(0, 0, sideA); assign(0, 1, sideA);
  assign(1, 0, cycle - sideA); assign(1, 1, cycle - sideA);

  return {
    cycle,
    sideA,
    offset: 0,
    offsetRef: 'TS2',
    coordinate: true,
    // "main-street through movements typically go here" -- the coordinated
    // phases are the ones that must run every cycle whether or not a detector
    // calls them, and on this network there are no detectors at all.
    minRecall: new Set((nema.barrier2Phases || []).filter(Boolean)),
    maxRecall: new Set(),
    phases,
    lockstep,
  };
}

const floorFor = (p) => p.yellow + p.red + Math.max(MIN_GREEN_FLOOR, p.minGreen);

export default function NemaProgramEditor({ nema, byNumber, movementsForPhase, onStage, onCancel, onHelp }) {
  const { sides, lockstep } = useMemo(() => layout(nema), [nema]);
  const [prg, setPrg] = useState(() => seed(nema, byNumber));

  const sideTotal = (s) => (s === 0 ? prg.sideA : prg.cycle - prg.sideA);

  /** Minimum this side can shrink to: the fuller ring decides. */
  const sideFloor = (s) => Math.max(
    ...[0, 1].map((r) => sides[s][r].reduce((t, n) => t + floorFor(prg.phases[n]), 0)),
    1,
  );

  const setPhaseField = (n, field, v) => setPrg((p) => {
    const ph = { ...p.phases[n], [field]: Math.max(0, Math.round(num(v, 0))) };
    // Clearance eats into green rather than into the split: the split is what
    // the barrier arithmetic depends on, so it must not move here.
    if (ph.split < floorFor(ph)) return p;
    return { ...p, phases: { ...p.phases, [n]: ph } };
  });

  /**
   * Move time between two adjacent phases of the same ring and side. The side
   * total is invariant, so the other ring is untouched and the barrier still
   * closes.
   */
  const nudgeSplit = (n, delta) => setPrg((p) => {
    let target = null;
    for (const s of [0, 1]) {
      for (const r of [0, 1]) {
        const list = sides[s][r];
        const i = list.indexOf(n);
        if (i === -1) continue;
        if (list.length < 2) return p;         // alone on its side: move the barrier
        target = list[(i + 1) % list.length];
        break;
      }
      if (target) break;
    }
    if (!target || target === n) return p;
    const a = { ...p.phases[n] }, b = { ...p.phases[target] };
    const room = Math.min(delta > 0 ? b.split - floorFor(b) : a.split - floorFor(a),
                          Math.abs(delta));
    if (room <= 0) return p;
    const d = Math.sign(delta) * room;
    a.split += d; b.split -= d;
    return { ...p, phases: { ...p.phases, [n]: a, [target]: b } };
  });

  /** Move the barrier: both rings, together, or it is not a barrier. */
  const nudgeBarrier = (delta) => setPrg((p) => {
    const next = Math.round(p.sideA + delta);
    const loA = sideFloor(0), loB = sideFloor(1);
    if (next < loA || p.cycle - next < loB) return p;
    const phases = { ...p.phases };
    for (const s of [0, 1]) {
      const want = s === 0 ? next : p.cycle - next;
      const have = s === 0 ? p.sideA : p.cycle - p.sideA;
      const diff = want - have;
      if (!diff) continue;
      for (const r of [0, 1]) {
        const list = sides[s][r].filter((n) => !(r === 1 && lockstep.has(n)));
        if (!list.length) continue;
        // Give the change to the last phase of the ring; it is the one a signal
        // engineer would flex, and it keeps every other split where the user put it.
        const last = list[list.length - 1];
        const ph = { ...phases[last], split: phases[last].split + diff };
        if (ph.split < floorFor(ph)) return p;
        phases[last] = ph;
      }
    }
    return { ...p, sideA: next, phases };
  });

  /** Rescale to a new cycle. Each ring is redistributed to hit its side total exactly,
   *  last phase takes the remainder. Scaling every split independently and re-deriving
   *  the barrier does not work — the rings round differently and the program stops closing. */
  const setCycle = (v) => setPrg((p) => {
    const cycle = Math.max(20, Math.min(300, Math.round(num(v, p.cycle))));
    const loA = sideFloor(0), loB = sideFloor(1);
    if (cycle < loA + loB) return p;
    const sideA = Math.max(loA, Math.min(cycle - loB, Math.round((p.sideA * cycle) / p.cycle)));
    const phases = { ...p.phases };
    for (const s of [0, 1]) {
      const total = s === 0 ? sideA : cycle - sideA;
      for (const r of [0, 1]) {
        const list = sides[s][r].filter((n) => !(r === 1 && lockstep.has(n)));
        if (!list.length) continue;
        const old = list.reduce((t, n) => t + p.phases[n].split, 0) || 1;
        let acc = 0;
        for (let i = 0; i < list.length; i++) {
          const n = list[i];
          const floor = floorFor(p.phases[n]);
          const want = i === list.length - 1
            ? total - acc
            : Math.max(floor, Math.round((p.phases[n].split * total) / old));
          if (want < floor) return p;
          phases[n] = { ...phases[n], split: want };
          acc += want;
        }
      }
    }
    return { ...p, cycle, sideA, phases };
  });

  const toggleRecall = (which, n) => setPrg((p) => {
    const s = new Set(p[which]);
    if (s.has(n)) s.delete(n); else s.add(n);
    return { ...p, [which]: s };
  });

  // Rows in cycle order, for both the chart and the list.
  const ordered = [];
  for (const s of [0, 1]) for (const r of [0, 1]) for (const n of sides[s][r]) {
    if (!ordered.includes(n)) ordered.push(n);
  }

  // --- chart -------------------------------------------------------------
  const W = 276, LBL = 30, ROW = 26, GAP = 3, TOP = 4, H = TOP + 2 * ROW + GAP + 14;
  const usable = W - LBL - 2;
  const pxPerSec = usable / prg.cycle;
  const barX = LBL + prg.sideA * pxPerSec;

  const ringCells = (r) => {
    const out = []; let x = LBL;
    for (const s of [0, 1]) {
      let cx = s === 0 ? LBL : barX;
      for (const n of sides[s][r]) {
        const ph = prg.phases[n];
        const w = ph.split * pxPerSec;
        const tall = lockstep.has(n);
        const y = TOP + (tall || r === 0 ? 0 : ROW + GAP);
        const h = tall ? 2 * ROW + GAP : ROW;
        if (!(tall && r === 1)) {
          out.push(
            <g key={`${r}-${n}`}>
              <title>{`φ${n} — split ${secs(ph.split)}, green `
                + `${secs(ph.split - ph.yellow - ph.red)}, yellow ${secs(ph.yellow)}, `
                + `all-red ${secs(ph.red)}`}</title>
              <rect x={cx + 1} y={y} width={Math.max(2, w - 2)} height={h} rx="3"
                fill="#4caf50" fillOpacity="0.22" stroke="#4caf50" strokeWidth="1.2" />
              {w > 26 && (
                <text x={cx + w / 2} y={y + h / 2 + 4} textAnchor="middle"
                  fill="#4caf50" fontSize="11" fontWeight="600">φ{n}</text>
              )}
            </g>,
          );
        }
        cx += w;
      }
      x = cx;
    }
    return out;
  };

  const drag = (onDelta) => (e) => {
    e.preventDefault();
    // The whole panel is draggable from any mousedown on it, so without this the
    // gesture moves the panel instead of the barrier and nothing appears to
    // happen.
    e.stopPropagation();
    const x0 = e.clientX;
    let last = 0;
    const move = (ev) => {
      const d = Math.round((ev.clientX - x0) / pxPerSec);
      if (d !== last) { onDelta(d - last); last = d; }
    };
    const up = () => {
      window.removeEventListener('mousemove', move);
      window.removeEventListener('mouseup', up);
    };
    window.addEventListener('mousemove', move);
    window.addEventListener('mouseup', up);
  };

  // Handles sit on the boundary between two phases of one ring, and on the
  // barrier. Dragging a boundary trades time between neighbours; dragging the
  // barrier moves both rings.
  const handles = [];
  for (const s of [0, 1]) for (const r of [0, 1]) {
    let cx = s === 0 ? LBL : barX;
    const list = sides[s][r];
    list.forEach((n, i) => {
      cx += prg.phases[n].split * pxPerSec;
      if (i < list.length - 1) {
        const y = TOP + (r === 1 && !lockstep.has(n) ? ROW + GAP : 0);
        handles.push(
          <rect key={`h-${s}-${r}-${n}`} x={cx - 3} y={y} width={6} height={ROW}
            fill="transparent" style={{ cursor: 'ew-resize' }}
            onMouseDown={drag((d) => nudgeSplit(n, d))} />,
        );
      }
    });
  }

  const stage = () => onStage({
    cycle: prg.cycle,
    offset: prg.offset,
    // SUMO's own param name for the cabinet convention.
    controllerType: prg.offsetRef === 'Type170' ? 'Type 170' : 'TS2',
    coordinate: prg.coordinate,
    ring1: nema.ring1, ring2: nema.ring2,
    barrierPhases: nema.barrierPhases, barrier2Phases: nema.barrier2Phases,
    minRecall: [...prg.minRecall].sort((a, b) => a - b),
    maxRecall: [...prg.maxRecall].sort((a, b) => a - b),
    phases: ordered.map((n) => {
      const p = prg.phases[n];
      return {
        nema_phase: n,
        state: (byNumber[n] || {}).state,
        minDur: Math.max(MIN_GREEN_FLOOR, p.minGreen),
        maxDur: p.split - p.yellow - p.red,
        yellow: p.yellow, red: p.red, vehExt: p.vehExt,
      };
    }),
  });

  return (
    <div>
      <div className="ne-slider-hint">
        Drag a divider to move green time between phases, or the barrier to move
        it between the two halves of the cycle.{' '}
        <button className="ne-link" onClick={onHelp}>How this works</button>
      </div>

      <div className="ne-phase-list">
        <div className="ne-phase-item">
          <svg width="100%" viewBox={`0 0 ${W} ${H}`}>
            <text x="0" y={TOP + ROW / 2 + 4} fill="#9a9a9a" fontSize="10">R1</text>
            <text x="0" y={TOP + ROW + GAP + ROW / 2 + 4} fill="#9a9a9a" fontSize="10">R2</text>
            {ringCells(0)}{ringCells(1)}
            <line x1={barX} y1={TOP - 3} x2={barX} y2={TOP + 2 * ROW + GAP + 3}
              stroke="#e0a13a" strokeWidth="2" />
            <rect x={barX - 4} y={TOP - 3} width={8} height={2 * ROW + GAP + 6}
              fill="transparent" style={{ cursor: 'ew-resize' }}
              onMouseDown={drag((d) => nudgeBarrier(d))} />
            <text x={LBL} y={H - 2} fill="#8a8a8a" fontSize="9">{secs(prg.sideA)}</text>
            <text x={W} y={H - 2} textAnchor="end" fill="#8a8a8a" fontSize="9">
              {secs(prg.cycle - prg.sideA)}
            </text>
          </svg>
          <div className="ne-phase-turns">
            Widths are to scale. Starting splits are an even division of the
            cycle — the source network carries none.
          </div>
        </div>
      </div>

      <div className="ne-phase-list">
        <div className="ne-phase-item">
          <div className="ne-phase-item-head">
            <span className="ne-phase-streets">Cycle</span>
            <input type="number" min="20" max="300" value={prg.cycle}
              onChange={(e) => setCycle(e.target.value)}
              style={{ width: 56 }} /> s
          </div>
        </div>
      </div>

      <Section title="Coordination &amp; recall">
      <div className="ne-phase-list">
        <div className="ne-phase-item">
          <div className="ne-phase-item-head">
            <span className="ne-phase-streets">Offset</span>
            <input type="number" min="0" max={prg.cycle} value={prg.offset}
              onChange={(e) => setPrg((p) => ({ ...p, offset: num(e.target.value) }))}
              style={{ width: 56 }} /> s
          </div>
          {/* TS2 references the start of the coordinated phase, Type 170 the start of yellow.
              Not a constant. */}
          <div className="ne-phase-turns">Offset is measured from</div>
          <select value={prg.offsetRef} style={{ width: '100%' }}
            onChange={(e) => setPrg((p) => ({ ...p, offsetRef: e.target.value }))}>
            <option value="TS2">TS2 — start of the coordinated phase</option>
            <option value="Type170">Type 170 — start of yellow</option>
          </select>
          <div className="ne-phase-item-head">
            <label>
              <input type="checkbox" checked={prg.coordinate}
                onChange={(e) => setPrg((p) => ({ ...p, coordinate: e.target.checked }))} />
              {' '}Coordinated (fixed cycle)
            </label>
          </div>
          <div className="ne-phase-turns">
            Uncoordinated, the cycle floats and the splits become ceilings rather
            than durations.
          </div>
        </div>
      </div>

      </Section>

      <Section title="Phase timing" count={ordered.length}>
      <div className="ne-phase-list">
        {ordered.map((n) => {
          const p = prg.phases[n];
          const green = p.split - p.yellow - p.red;
          const { names, turns } = movementsForPhase((byNumber[n] || {}).state || '');
          const alone = [0, 1].some((s) => [0, 1].some((r) => {
            const l = sides[s][r]; return l.includes(n) && l.length === 1;
          }));
          return (
            <div key={n} className="ne-phase-item">
              <div className="ne-phase-item-head">
                <span className="ne-phase-dot" style={{ background: '#4caf50' }} />
                <span className="ne-phase-streets">{names.join(' + ') || `Phase ${n}`}</span>
                <span className="ne-slider-val">φ{n}</span>
              </div>
              {turns && <div className="ne-phase-turns">{turns}</div>}
              <div className="ne-phase-item-head">
                <span className="ne-phase-streets">Split</span>
                <button className="ne-link" disabled={alone}
                  onClick={() => nudgeSplit(n, -1)}>−</button>
                <span className="ne-slider-val">{secs(p.split)}</span>
                <button className="ne-link" disabled={alone}
                  onClick={() => nudgeSplit(n, +1)}>+</button>
                <span className="ne-phase-bounds">green {secs(green)}</span>
              </div>
              {alone && (
                <div className="ne-phase-turns">
                  The only phase its ring serves on this side, so its split is the
                  side itself — move the barrier to change it.
                </div>
              )}
              <div className="ne-phase-item-head">
                <label>min green <input type="number" min={MIN_GREEN_FLOOR} max="60"
                  value={p.minGreen} style={{ width: 44 }}
                  onChange={(e) => setPhaseField(n, 'minGreen', e.target.value)} /></label>
                <label>yellow <input type="number" min="0" max="10" value={p.yellow}
                  style={{ width: 40 }}
                  onChange={(e) => setPhaseField(n, 'yellow', e.target.value)} /></label>
                <label>all-red <input type="number" min="0" max="10" value={p.red}
                  style={{ width: 40 }}
                  onChange={(e) => setPhaseField(n, 'red', e.target.value)} /></label>
                <label>veh ext <input type="number" min="0" max="10" value={p.vehExt}
                  style={{ width: 40 }}
                  onChange={(e) => setPhaseField(n, 'vehExt', e.target.value)} /></label>
              </div>
              <div className="ne-phase-meta">
                <label className="ne-phase-tag">
                  <input type="checkbox" checked={prg.minRecall.has(n)}
                    onChange={() => toggleRecall('minRecall', n)} /> min recall
                </label>
                <label className="ne-phase-tag">
                  <input type="checkbox" checked={prg.maxRecall.has(n)}
                    onChange={() => toggleRecall('maxRecall', n)} /> max recall
                </label>
              </div>
            </div>
          );
        })}
      </div>

      </Section>

      <button className="ne-action stage" onClick={stage}>Stage this program</button>
      <button className="ne-action secondary" onClick={onCancel}>Cancel</button>
    </div>
  );
}
